"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { BadgeCheck, Building2, CircleCheck as CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Certifications() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const grid = useRevealOnScroll<HTMLDivElement>();
  const quote = useRevealOnScroll<HTMLDivElement>();
  const footer = useRevealOnScroll<HTMLDivElement>();
  const certifications = [{
    name: "ISO 9001:2015 Certified",
    issuer: "Quality Management Systems",
    year: "2024",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/iso_certification_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/iso_certification_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/iso_certification_badge.png-wvc-sizes"
    },
    alt: "ISO 9001 quality management certification badge"
  }, {
    name: "ISO 14001:2015 Certified",
    issuer: "QUALITY MANAGEMENT SYSTEMS",
    year: "2024",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/aerospace_standard_badge.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/aerospace_standard_badge.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/aerospace_standard_badge.webp-wvc-sizes"
    },
    alt: "AS9100 aerospace quality standard badge"
  }, {
    name: "Precision Tolerance Grade",
    issuer: "Industrial Manufacturing Standards",
    year: "2024",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/precision_tolerance_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/precision_tolerance_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/precision_tolerance_badge.png-wvc-sizes"
    },
    alt: "Precision tolerance manufacturing badge"
  }, {
    name: "North American Compliance",
    issuer: "Industrial Safety & Compliance",
    year: "2024",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/na_compliance_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/na_compliance_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/na_compliance_badge.png-wvc-sizes"
    },
    alt: "North American industrial compliance badge"
  }];
  const trustSignals = [{
    icon: Building2,
    label: "B2B Manufacturing Partner"
  }, {
    icon: ShieldCheck,
    label: "Quality Assured"
  }, {
    icon: BadgeCheck,
    label: "Precision Certified"
  }, {
    icon: Sparkles,
    label: "Efficiency Optimized"
  }];
  return <section data-section-id="312" id="quality-certifications" className="relative overflow-hidden bg-background text-foreground py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/3 rounded-lg bg-primary/15 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-primary">
            Engineering Standards
            <Sparkles className="size-3.5" />
          </span>
          <h2 className="mt-5 font-default text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground">
            Engineered for <em className="font-default italic text-primary">industrial excellence</em>.
          </h2>
          <p className="mt-6 font-default text-base sm:text-lg leading-relaxed text-muted-foreground">
            Global Standard Technology maintains rigorous quality protocols to ensure 
            precision in every die-cut, slit, and laminate. Our certifications reflect 
            our commitment to the exacting requirements of the aerospace, automotive, 
            and electronics sectors.
          </p>
        </div>

        <div ref={grid.ref} className={`mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 transition-all duration-700 ease-out delay-150 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {certifications.map((cert, i) => <Card key={cert.name} data-index={i} className="group relative border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <CardContent className="flex flex-col items-center px-6 pt-8 pb-7 text-center">
                <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-accent/60">
                  <img src={cert.image.src} data-wvc-srcset={cert.image.srcset} data-wvc-sizes={cert.image.sizes} alt={cert.alt} loading="lazy" decoding="async" className="h-20 w-20 object-contain" />
                </div>

                <h3 className="mt-6 font-default text-lg font-semibold leading-snug text-foreground">
                  {cert.name}
                </h3>

                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  {cert.issuer}
                </p>

                <Badge variant="outline" className="mt-5 gap-1.5 border-primary/30 bg-primary/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-primary">
                  <CheckCircle2 className="size-3" />
                  Verified {cert.year}
                </Badge>
              </CardContent>
            </Card>)}
        </div>

        <div ref={quote.ref} className={`mt-20 transition-all duration-700 ease-out delay-200 ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Card className="relative overflow-hidden border-0 bg-primary shadow-xl">
            <CardContent className="relative px-8 py-12 sm:px-14 sm:py-16">
              <div className="mx-auto max-w-3xl text-center">
                <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-primary-foreground/80">
                  Quality Philosophy
                </span>
                <blockquote className="mt-6 font-default text-2xl sm:text-3xl lg:text-4xl font-medium leading-[1.25] tracking-tight text-primary-foreground">
                  "Precision is not just a metric; it is the foundation of our 
                  manufacturing integrity. We hold ourselves to these standards 
                  because our partners in aerospace and automotive demand 
                  nothing less than absolute consistency."
                </blockquote>

                <div className="mt-8 flex items-center justify-center gap-3">
                  <SafeHydrate><Avatar className="size-12 ring-2 ring-primary-foreground/30">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/gst_leadership_avatar.webp" alt="Global Standard Technology Leadership" src="https://gstna.com/wp-content/uploads/2026/07/president.png" className="!w-[48px] !h-[49px]" />
                    
                  </Avatar></SafeHydrate>
                  <div className="text-left">
                    <p className="font-default text-base font-semibold text-primary-foreground">YT Kim, CEO</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-primary-foreground/70">
                      Global Standard Technology
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div ref={footer.ref} className={`mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 transition-all duration-700 ease-out delay-300 ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {trustSignals.map((signal, i) => {
          const Icon = signal.icon;
          return <div key={signal.label} data-index={i} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground">
                <Icon className="size-3.5 text-primary" />
                <span>{signal.label}</span>
              </div>;
        })}
        </div>
      </div>
    </section>;
}